import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Ticket, Trash2, CalendarClock, Truck, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API } from "../lib/api";
import { useAuth } from "@/lib/AuthContext";
import MaterialTicketLines from "./MaterialTicketLines";
import DeliveryWorkspace from "./DeliveryWorkspace";

export const canVerifyDelivery = (t) =>
  t.status !== "Closed"
  && (t.type === "direct_ship" || ["Pulled", "Ready for Delivery", "Delivered"].includes(t.status));

const STATUS_TONE = {
  Requested: "bg-amber-100 text-amber-800", Pulled: "bg-blue-100 text-blue-800",
  "Ready for Delivery": "bg-indigo-100 text-indigo-800",
  Delivered: "bg-emerald-100 text-emerald-800", Closed: "bg-gray-100 text-gray-700",
};
const LINE_TONE = {
  "Not On Hand": "bg-amber-100 text-amber-800", "Not Ordered": "bg-red-100 text-red-800",
  "Short Delivered": "bg-red-100 text-red-800",
};

// FLD-1 — the tickets a foreman created, with their real line records
// (keyed on the stable FRS item key) and the Not On Hand / Not Ordered flags.
export default function MaterialTicketsPanel({ projectId, focusTicket = "", search = "" }) {
  const { user } = useAuth();
  const actor = user?.email || user?.name || "";
  const [allTickets, setTickets] = useState([]);
  const q = search.trim().toLowerCase();
  const tickets = q ? allTickets.filter((t) => [t.ticket_number, t.type, t.status, t.requested_by,
    ...(t.rollup?.scopes || []).map((s) => s.scope_type)].some((v) => String(v || "").toLowerCase().includes(q))) : allTickets;
  const [lines, setLines] = useState({});
  const [open, setOpen] = useState("");
  const [loading, setLoading] = useState(true);
  const [deliverFor, setDeliverFor] = useState(null);
  const [pullBackFor, setPullBackFor] = useState(null);
  const [needBy, setNeedBy] = useState(null);        // { ticket, value }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/material/tickets", { params: { project_id: projectId } });
      setTickets(data || []);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // "Chase from the grid": the FRS grid links here with ?ticket=PT-040
  useEffect(() => {
    if (!focusTicket || tickets.length === 0) return;
    const hit = tickets.find((t) => t.ticket_number === focusTicket);
    if (hit && open !== hit.id) toggle(hit);
  }, [focusTicket, tickets]); // eslint-disable-line

  const openLines = async (id) => {
    try {
      const { data } = await api.get(`/material/tickets/${id}/lines`);
      setLines((cur) => ({ ...cur, [id]: data || [] }));
    } catch { setLines((cur) => ({ ...cur, [id]: [] })); }
  };

  const pullBack = async () => {
    const t = pullBackFor;
    if (!t) return;
    try {
      const { data } = await api.delete(`/material/tickets/${t.id}`, { params: { actor } });
      toast.success(`${t.ticket_number} pulled back`, {
        description: data.returned_to_on_hand
          ? `${data.returned_to_on_hand} unit(s) went back on hand` : "Nothing had been pulled yet",
      });
      setPullBackFor(null);
      setOpen("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not pull the ticket back");
    }
  };

  const saveNeedBy = async () => {
    const { ticket, value } = needBy || {};
    if (!ticket || value === ticket.need_by) { setNeedBy(null); return; }
    try {
      await api.patch(`/material/tickets/${ticket.id}`, { need_by: value, actor });
      toast.success("Need-by updated");
      setNeedBy(null);
      await openLines(ticket.id);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not change the need-by date");
    }
  };

  const toggle = async (t) => {
    if (open === t.id) { setOpen(""); return; }
    setOpen(t.id);
    if (!lines[t.id]) await openLines(t.id);
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto" data-testid="material-tickets-panel">
      <table className="w-full text-xs">
        <thead className="bg-gray-100 text-gray-600 uppercase text-[10px]">
          <tr>
            <th className="p-2"></th><th className="text-left p-2">Ticket</th>
            <th className="text-left p-2">Type</th><th className="text-left p-2">Scopes</th>
            <th className="text-left p-2">Need by</th>
            <th className="text-right p-2">Lines</th><th className="text-right p-2">Requested</th>
            <th className="text-right p-2">Pulled</th><th className="text-right p-2">Delivered</th>
            <th className="text-left p-2">Not on hand</th><th className="text-left p-2">Status</th>
            <th className="text-left p-2">Requested by</th><th className="p-2"></th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <React.Fragment key={t.id}>
              <tr className="border-t border-gray-100" data-testid={`material-ticket-${t.ticket_number}`}>
                <td className="p-2">
                  <button onClick={() => toggle(t)} data-testid={`material-ticket-expand-${t.ticket_number}`}>
                    {open === t.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </td>
                <td className="p-2 font-mono font-semibold">{t.ticket_number}</td>
                <td className="p-2">{t.type === "direct_ship" ? "Direct ship" : "Warehouse"}</td>
                <td className="p-2" data-testid={`material-ticket-scopes-${t.ticket_number}`}>
                  <span className="flex flex-wrap gap-1">
                    {(t.rollup?.scopes || []).map((s) => (
                      <span key={s.scope_type} title={`${s.units} unit(s) of ${s.scope_type}`}
                            className="px-1 rounded text-[10px] font-semibold bg-blue-100 text-blue-900">
                        {s.scope_type} <span className="font-normal">{s.units}</span>
                      </span>
                    ))}
                    {(t.rollup?.scopes || []).length === 0 && <span className="text-gray-400">—</span>}
                  </span>
                </td>
                <td className="p-2">{t.need_by || "—"}</td>
                <td className="p-2 text-right tabular-nums">{t.rollup?.lines || 0}</td>
                <td className="p-2 text-right tabular-nums">{t.rollup?.requested || 0}</td>
                <td className="p-2 text-right tabular-nums">{t.rollup?.pulled || 0}</td>
                <td className="p-2 text-right tabular-nums">{t.rollup?.delivered || 0}</td>
                <td className="p-2">
                  {t.rollup?.not_on_hand > 0 && (
                    <span className="px-1 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 mr-1">
                      {t.rollup.not_on_hand} not on hand
                    </span>
                  )}
                  {t.rollup?.not_ordered > 0 && (
                    <span className="px-1 rounded text-[10px] font-semibold bg-red-100 text-red-800">
                      {t.rollup.not_ordered} not ordered
                    </span>
                  )}
                </td>
                <td className="p-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_TONE[t.status] || "bg-gray-100 text-gray-700"}`}>
                    {t.status}
                  </span>
                </td>
                <td className="p-2">{t.requested_by || t.created_by || "—"}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  {canVerifyDelivery(t) && (
                    <button onClick={() => setDeliverFor(t)}
                            title="Verify what arrived and record where it was placed"
                            data-testid={`material-ticket-verify-${t.ticket_number}`}
                            className="text-blue-700 mr-2 align-middle">
                      <Truck className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {["Requested", "Pulled", "Ready for Delivery"].includes(t.status) ? (
                    <>
                      <button onClick={() => setNeedBy({ ticket: t, value: t.need_by || "" })}
                              title="Change the need-by date"
                              data-testid={`material-ticket-editdate-${t.ticket_number}`}
                              className="text-blue-700 mr-2 align-middle">
                        <CalendarClock className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setPullBackFor(t)}
                              title="Pull this ticket back completely — anything pulled goes back on hand"
                              data-testid={`material-ticket-pullback-${t.ticket_number}`}
                              className="text-red-600 align-middle">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      {t.status === "Closed" ? (
                        <button
                          onClick={() => window.open(
                            `${API}/delivery/tickets/${t.id}/receipt?auth=${encodeURIComponent(localStorage.getItem("auth_token") || "")}`,
                            "_blank", "noopener")}
                          title="Printable delivery receipt — hand it to the GC"
                          data-testid={`material-ticket-receipt-${t.ticket_number}`}
                          className="text-blue-700 align-middle">
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <span className="text-[10px] text-gray-400">delivered</span>
                      )}
                    </>
                  )}
                </td>
              </tr>
              {open === t.id && (
                <tr className="bg-gray-50">
                  <td colSpan={13} className="p-3" data-testid={`material-ticket-lines-${t.ticket_number}`}>
                    <MaterialTicketLines
                      ticket={t}
                      lines={lines[t.id] || []}
                      actor={actor}
                      canEdit={["Requested", "Pulled", "Ready for Delivery"].includes(t.status)}
                      onChanged={async () => { await openLines(t.id); load(); }}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {tickets.length === 0 && (
            <tr><td colSpan={11} className="p-6 text-center text-gray-400" data-testid="material-tickets-empty">
              {loading ? <Loader2 className="w-4 h-4 animate-spin inline" />
                : <span className="inline-flex items-center gap-1"><Ticket className="w-3.5 h-3.5" />
                    No pull tickets yet — request material from the FRS grid.</span>}
            </td></tr>
          )}
        </tbody>
      </table>

      {deliverFor && (
        <DeliveryWorkspace ticketId={deliverFor.id} ticketNumber={deliverFor.ticket_number} actor={actor}
                        onClose={() => setDeliverFor(null)}
                        onChanged={async () => { await openLines(deliverFor.id); load(); }} />
      )}

      <Dialog open={!!needBy} onOpenChange={(v) => { if (!v) setNeedBy(null); }}>
        <DialogContent className="max-w-sm" data-testid="material-needby-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Need material by — <span className="font-mono">{needBy?.ticket?.ticket_number}</span>
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              The date cascades onto every line on this ticket.
            </DialogDescription>
          </DialogHeader>
          <Input type="date" value={needBy?.value || ""} data-testid="material-needby-input"
                 onChange={(e) => setNeedBy((c) => ({ ...c, value: e.target.value }))} />
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setNeedBy(null)}
                    data-testid="material-needby-cancel">Cancel</Button>
            <Button size="sm" onClick={saveNeedBy} data-testid="material-needby-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pullBackFor} onOpenChange={(v) => { if (!v) setPullBackFor(null); }}>
        <DialogContent className="max-w-md" data-testid="material-pullback-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Pull <span className="font-mono">{pullBackFor?.ticket_number}</span> back completely?
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              The ticket and all {pullBackFor?.rollup?.lines || 0} line(s) are removed. Anything the
              warehouse already pulled goes back on hand.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setPullBackFor(null)}
                    data-testid="material-pullback-cancel">Keep it</Button>
            <Button size="sm" variant="destructive" onClick={pullBack}
                    data-testid="material-pullback-confirm">Pull it back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
